import React, {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Visibility } from "@mui/icons-material";
import { Icon, IconButton } from "@mui/material";

import TableServer from "@/components/tables/TableServer";
import useFetcher from "@/helpers/hooks/useFetcher";
import { requestService } from "../services/requestService";
import RpMUIModal from "@/components/modals/RpMUIModal";
import RequestDetails from "./RequestDetails";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { USER_ROLE } from "@/config/constants";

type BrandRequestRow = {
  id?: string | number;
  system_generated_request_id?: string;
  brand_request_type?: string;
  user_name?: string;
  submitted_date?: string;
  [key: string]: unknown;
};

type BrandRequestListResponse = {
  data?: BrandRequestRow[];
  meta?: {
    total_pages?: number;
    total?: number;
  };
};

type RequestListingFilters = {
  page: number;
  row: number;
  sortBy: string;
  sortByDir: string;
  search?: string;
};

type TableFetchPayload = {
  pageIndex: number;
  pageSize: number;
  searchKey?: string;
  sortBy?: string;
  sortByDir?: string;
};

type TableCellProps = {
  value?: ReactNode;
  row?: {
    original?: BrandRequestRow;
  };
};

type SelectedBrand = {
  id?: string | number;
};

// Styles object
const styles: Record<string, CSSProperties> = {
  tableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "initial",
    minHeight: "36px",
  },
};

function RequestListing() {
  const { selectedBrand, loggedInUserRole } = useContext(GlobalContext);
  const brand = selectedBrand as SelectedBrand | null | undefined;
  const isParentUserRole =
    loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
    loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
    loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME;
  const loader = useContext(LoaderContext);
  const [open, setOpen] = useState<string | null>(null);

  const [filters, setFilters] = useState<RequestListingFilters>({
    page: 1,
    row: 10,
    sortBy: "",
    sortByDir: "",
  });
  const payload = {
    ...(isParentUserRole ? { fk_brand_id: brand?.id } : {}),
    ...filters,
  };

  const { data, isLoading, isValidating } =
    useFetcher<BrandRequestListResponse>(
      payload,
      requestService.getBrandRequestListing,
      {
        revalidateOnFocus: false,
      }
    );

  useEffect(() => {
    loader(isLoading || isValidating);
  }, [loader, isLoading, isValidating]);

  const resData = data?.data || [];
  const { total_pages, total } = data?.meta || {};

  const columns = useMemo(
    () => [
      {
        Header: "Request Id",
        accessor: "system_generated_request_id",
        width: 200,
        Cell: ({ value }: TableCellProps) => (
          <div style={styles.tableCell}>{value}</div>
        ),
      },
      {
        Header: "Request Type",
        accessor: "brand_request_type",
        width: 200,
        Cell: ({ value }: TableCellProps) => (
          <div style={styles.tableCell}>{value}</div>
        ),
      },
      {
        Header: "Submitted By",
        accessor: "user_name",
        width: 200,
        Cell: ({ value }: TableCellProps) => (
          <div style={styles.tableCell}>{value}</div>
        ),
      },
      {
        Header: "Submitted On",
        accessor: "submitted_date",
        width: 200,
        Cell: ({ value }: TableCellProps) => (
          <div style={styles.tableCell}>{value}</div>
        ),
      },
      {
        Header: "View",
        accessor: "view",
        width: 100,
        disableSortBy: true,
        Cell: ({ row }: TableCellProps) => (
          <div style={styles.tableCell}>
            <IconButton
              onClick={() => {
                const id = row?.original?.id;
                setOpen(id == null ? null : String(id));
              }}
              color="inherit"
              className="p-0"
            >
              <Visibility />
            </IconButton>
          </div>
        ),
      },
    ],
    []
  );

  const handleCloseModal = () => {
    setOpen(null);
  };

  const handleFetchData = useCallback(
    ({
      pageIndex,
      pageSize,
      searchKey,
      sortBy = "",
      sortByDir = "",
    }: TableFetchPayload) => {
      setFilters((p) => ({
        ...p,
        page: pageIndex + 1,
        row: pageSize,
        sortBy: sortBy,
        sortByDir: sortByDir,
        search: searchKey,
      }));
    },
    []
  );

  return (
    <div className="p-0">
      <TableServer
        columns={columns}
        data={resData}
        showSearch={false}
        fetchData={handleFetchData}
        isLoading={isLoading}
        pageCount={total_pages}
        // hideSearchLabel={hideSearchLabel}
        // filters={filters}
        totalRecords={total}
        // initialPageSize={}
        // useLayout
        containerClass="border-left-0 border-right-0 border-top-0"
      />

      {!!open && (
        <RpMUIModal open={!!open} onClose={handleCloseModal} scroll="body">
          <div className="d-flex justify-content-end align-items-center">
            <Icon
              src="/img/icons/black-cross-icon.png"
              alt="close"
              onClick={handleCloseModal}
              component="img"
              className="cursor-p"
            />
          </div>
          <RequestDetails id={open} />
        </RpMUIModal>
      )}
    </div>
  );
}

export default RequestListing;
